// src/pages/Dashboard.js

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  addTrade,
  updateTrade,
  deleteTrade,
  onTradesSnapshot,
  saveTags,
  getSavedTags,
  saveRules,
  getRules,
  saveRulesHistory,
  getRulesHistory,
  getAccounts,
  saveAccounts
} from "../services/tradeService";
import DashboardUI from "../components/DashboardUI";

export default function Dashboard() {
  const { currentUser, logout, getUserSettings, updateUserSettings } = useAuth();
  const [trades, setTrades] = useState([]);
  const [savedTags, setSavedTags] = useState([]);
  const [settings, setSettings] = useState({
    startBal: 50000,
    beCap: 35,
    ratingType: "stars",
    userName: "",
    theme: localStorage.getItem("theme") || "dark"
  });
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [rules, setRules] = useState([]);
  const [rulesHistory, setRulesHistory] = useState({});
  const [accounts, setAccounts] = useState([{ id: "default", name: "Main Account" }]);
  const [currentAccountId, setCurrentAccountId] = useState("default");

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    async function loadUserData() {
      try {
        const [userSettings, tags, savedRules, savedRulesHistory, savedAccounts] = await Promise.all([
          getUserSettings(),
          getSavedTags(currentUser.uid),
          getRules(currentUser.uid),
          getRulesHistory(currentUser.uid),
          getAccounts(currentUser.uid)
        ]);

        if (userSettings) {
          setSettings((prev) => ({
            ...prev,
            ...userSettings,
            theme: userSettings?.theme || localStorage.getItem("theme") || "dark"
          }));
        }
        if (tags) setSavedTags(tags);
        if (savedRules) setRules(savedRules);
        if (savedRulesHistory) setRulesHistory(savedRulesHistory);
        if (savedAccounts && savedAccounts.length > 0) {
          setAccounts(savedAccounts);
          setCurrentAccountId(savedAccounts[0].id);
        }
      } catch (err) {
        console.error("Failed to load user data:", err);
      }
      setSettingsLoaded(true);
      setLoading(false);
    }

    loadUserData();
  }, [currentUser]);

    useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const unsubscribe = onTradesSnapshot(currentUser.uid, currentAccountId, (tradesList) => {
      setTrades(tradesList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, currentAccountId]);

  const handleSaveTrade = useCallback(
    async (tradeData, isEdit) => {
      try {
        if (isEdit && tradeData.id) {
          const { id, ...data } = tradeData;
          await updateTrade(currentUser.uid, id, data, currentAccountId);
        } else {
          await addTrade(currentUser.uid, tradeData, currentAccountId);
        }
      } catch (err) {
        console.error("Failed to save trade:", err);
        alert("Failed to save trade. Please try again.");
      }
    },
    [currentUser, currentAccountId]
  );

  const handleDeleteTrade = useCallback(
    async (tradeId) => {
      try {
        await deleteTrade(currentUser.uid, tradeId, currentAccountId);
      } catch (err) {
        console.error("Failed to delete trade:", err);
        alert("Failed to delete trade.");
      }
    },
    [currentUser, currentAccountId]
  );

  const handleSaveSettings = useCallback(
    async (newSettings) => {
      try {
        setSettings((prev) => ({ ...prev, ...newSettings }));
        await updateUserSettings(newSettings);
      } catch (err) {
        console.error("Failed to save settings:", err);
      }
    },
    [currentUser]
  );

  const handleSaveTags = useCallback(
    async (newTags) => {
      try {
        setSavedTags(newTags);
        await saveTags(currentUser.uid, newTags);
      } catch (err) {
        console.error("Failed to save tags:", err);
      }
    },
    [currentUser]
  );
useEffect(() => {
  if (!settingsLoaded) return;

  document.body.classList.remove("dark", "light");
  document.body.classList.add(settings.theme || "dark");

  console.log("Theme:", settings.theme);
}, [settings.theme, settingsLoaded]);
  const handleSaveRules = useCallback(
  async (newRules) => {
    try {
      console.log("handleSaveRules called with:", newRules, "uid:", currentUser?.uid);
      setRules(newRules);
      await saveRules(currentUser.uid, newRules);
    } catch (err) {
      console.error("Failed to save rules:", err);
    }
  },
  [currentUser]
);

  const handleSaveRulesHistory = useCallback(
    async (newHistory) => {
      try {
        setRulesHistory(newHistory);
        await saveRulesHistory(currentUser.uid, newHistory);
      } catch (err) {
        console.error("Failed to save rules history:", err);
      }
    },
    [currentUser]
  );

  const handleSwitchAccount = useCallback((accountId) => {
    setCurrentAccountId(accountId);
    setTrades([]);
  }, []);

  const handleAddAccount = useCallback(async (name, updatedList) => {
    // If updatedList passed, it's a rename operation
    if (updatedList) {
      setAccounts(updatedList);
      try { await saveAccounts(currentUser.uid, updatedList); } catch (err) { console.error("Failed to rename account:", err); }
      return;
    }
    if (accounts.length >= 3) return;
    const newId = `account_${Date.now()}`;
    const newAccounts = [...accounts, { id: newId, name: name || `Account ${accounts.length + 1}` }];
    setAccounts(newAccounts);
    setCurrentAccountId(newId);
    setTrades([]);
    try {
      await saveAccounts(currentUser.uid, newAccounts);
    } catch (err) {
      console.error("Failed to save accounts:", err);
    }
  }, [accounts, currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0B0E",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 12,
          fontFamily: "'Inter',sans-serif"
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "linear-gradient(135deg,#22C55E,#16A34A)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "pulse 1.5s infinite"
          }}
        >
          <svg width={22} height={22} viewBox="0 0 100 100" fill="white">
            <path d="M55 5L25 55h20l-5 40 35-50H55l10-40z" />
          </svg>
        </div>
        <div style={{ color: "#4B5563", fontSize: 13 }}>Loading your journal...</div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
      </div>
    );
  }

  if (!settingsLoaded) return null;

  return (
    <DashboardUI
      trades={trades}
      savedTags={savedTags}
      settings={settings}
      userName={settings.userName || currentUser.displayName || ""}
      onSaveTrade={handleSaveTrade}
      onDeleteTrade={handleDeleteTrade}
      onSaveSettings={handleSaveSettings}
      onSaveTags={handleSaveTags}
      onLogout={handleLogout}
      rules={rules}
      rulesHistory={rulesHistory}
      onSaveRules={handleSaveRules}
      onSaveRulesHistory={handleSaveRulesHistory}
      allAccounts={accounts}
      currentAccountId={currentAccountId}
      onSwitchAccount={handleSwitchAccount}
      onAddAccount={handleAddAccount}
    />
  );
}